/**
 * Read-only market intelligence for the Markets screen.
 *
 * Every source is a public, key-less endpoint called straight from the client:
 * no account data, addresses or balances are ever sent. Each fetch is isolated
 * so one unreachable provider degrades a single card instead of the screen.
 */

const CG = "https://api.coingecko.com/api/v3";
const LLAMA = "https://api.llama.fi";

async function getJson<T>(url: string, timeoutMs = 12_000): Promise<T | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch(url, {
      signal: controller.signal,
      headers: { accept: "application/json" },
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export interface XrpMarket {
  price: number;
  change1h: number | null;
  change24h: number | null;
  change7d: number | null;
  change30d: number | null;
  marketCap: number;
  marketCapRank: number | null;
  volume24h: number;
  high24h: number | null;
  low24h: number | null;
  circulating: number | null;
  total: number | null;
  ath: number | null;
  athChange: number | null;
  fdv: number | null;
  updatedAt: string | null;
}

interface CgCoin {
  market_cap_rank?: number;
  last_updated?: string;
  market_data?: {
    current_price?: Record<string, number>;
    market_cap?: Record<string, number>;
    fully_diluted_valuation?: Record<string, number>;
    total_volume?: Record<string, number>;
    high_24h?: Record<string, number>;
    low_24h?: Record<string, number>;
    ath?: Record<string, number>;
    ath_change_percentage?: Record<string, number>;
    price_change_percentage_1h_in_currency?: Record<string, number>;
    price_change_percentage_24h_in_currency?: Record<string, number>;
    price_change_percentage_7d_in_currency?: Record<string, number>;
    price_change_percentage_30d_in_currency?: Record<string, number>;
    circulating_supply?: number;
    total_supply?: number;
  };
}

export async function fetchXrpMarket(): Promise<XrpMarket | null> {
  const data = await getJson<CgCoin>(
    `${CG}/coins/ripple?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false`,
  );
  const m = data?.market_data;
  if (!m?.current_price?.["usd"]) return null;
  return {
    price: m.current_price["usd"],
    change1h: m.price_change_percentage_1h_in_currency?.["usd"] ?? null,
    change24h: m.price_change_percentage_24h_in_currency?.["usd"] ?? null,
    change7d: m.price_change_percentage_7d_in_currency?.["usd"] ?? null,
    change30d: m.price_change_percentage_30d_in_currency?.["usd"] ?? null,
    marketCap: m.market_cap?.["usd"] ?? 0,
    marketCapRank: data?.market_cap_rank ?? null,
    volume24h: m.total_volume?.["usd"] ?? 0,
    high24h: m.high_24h?.["usd"] ?? null,
    low24h: m.low_24h?.["usd"] ?? null,
    circulating: m.circulating_supply ?? null,
    total: m.total_supply ?? null,
    ath: m.ath?.["usd"] ?? null,
    athChange: m.ath_change_percentage?.["usd"] ?? null,
    fdv: m.fully_diluted_valuation?.["usd"] ?? null,
    updatedAt: data?.last_updated ?? null,
  };
}

export interface XrplEcosystem {
  marketCap: number | null;
  change24h: number | null;
  volume24h: number | null;
}

interface CgCategory {
  id: string;
  market_cap?: number | null;
  market_cap_change_24h?: number | null;
  volume_24h?: number | null;
}

/** Market cap of the XRP Ledger ecosystem category only — no unrelated sectors. */
export async function fetchXrplEcosystem(): Promise<XrplEcosystem | null> {
  const list = await getJson<CgCategory[]>(`${CG}/coins/categories?order=market_cap_desc`);
  const hit = list?.find((c) => c.id === "xrp-ledger-ecosystem");
  if (!hit) return null;
  return {
    marketCap: hit.market_cap ?? null,
    change24h: hit.market_cap_change_24h ?? null,
    volume24h: hit.volume_24h ?? null,
  };
}

export interface XrplRwaSnapshot {
  /** Total DeFi TVL on the XRP Ledger. */
  xrplTvl: number | null;
  /**
   * Distributed RWA — the value of tokenized assets actually issued and
   * deployed on the XRP Ledger (each issuer's XRPL chain slice).
   */
  distributedTvl: number | null;
  /**
   * Represented RWA — the full underlying real-world asset value of the
   * protocols that deploy on the XRPL, across every chain they operate on.
   */
  representedTvl: number | null;
  /** Those XRPL RWA protocols, largest XRPL footprint first. */
  issuers: {
    name: string;
    category: string;
    /** Value distributed on the XRPL. */
    distributed: number;
    /** Total value represented across all chains. */
    represented: number;
  }[];
}

interface LlamaChain {
  name: string;
  tvl: number | null;
}

interface LlamaProtocol {
  name: string;
  category?: string;
  tvl?: number | null;
  chains?: string[];
  chainTvls?: Record<string, number>;
}

const XRPL_CHAIN_NAMES = ["xrpl", "xrp ledger", "ripple", "xrp"];
const RWA_CATEGORIES = ["rwa", "rwa lending", "treasury", "stablecoins"];

function isXrplChain(name: string): boolean {
  return XRPL_CHAIN_NAMES.includes(name.trim().toLowerCase());
}

/** XRPL-scoped tokenized real-world-asset activity. Nothing from other chains. */
export async function fetchXrplRwa(): Promise<XrplRwaSnapshot> {
  const [chains, protocols] = await Promise.all([
    getJson<LlamaChain[]>(`${LLAMA}/v2/chains`),
    getJson<LlamaProtocol[]>(`${LLAMA}/protocols`),
  ]);

  const xrplTvl = chains?.find((c) => isXrplChain(c.name))?.tvl ?? null;

  const xrplRwa = (protocols ?? []).filter(
    (p) =>
      (p.chains ?? []).some(isXrplChain) &&
      RWA_CATEGORIES.includes((p.category ?? "").toLowerCase()),
  );

  // Distributed = the XRPL chain slice; represented = the protocol's global
  // total (the full underlying asset value it tokenizes).
  const issuers = xrplRwa
    .map((p) => {
      const perChain = p.chainTvls ?? {};
      const xrplKey = Object.keys(perChain).find(isXrplChain);
      const represented = p.tvl ?? 0;
      return {
        name: p.name,
        category: p.category ?? "RWA",
        distributed: xrplKey ? (perChain[xrplKey] ?? 0) : represented,
        represented,
      };
    })
    .sort((a, b) => b.distributed - a.distributed);

  const distributedTvl = issuers.length
    ? issuers.reduce((sum, i) => sum + i.distributed, 0)
    : null;
  const representedTvl = issuers.length
    ? issuers.reduce((sum, i) => sum + i.represented, 0)
    : null;

  return { xrplTvl, distributedTvl, representedTvl, issuers: issuers.slice(0, 8) };
}

export interface LedgerPulse {
  ledgerIndex: number | null;
  baseFeeXrp: number | null;
  reserveBaseXrp: number | null;
  reserveIncXrp: number | null;
  loadFactor: number | null;
  version: string | null;
  peers: number | null;
  completeLedgers: string | null;
}

export async function fetchLedgerPulse(): Promise<LedgerPulse | null> {
  const { getClient } = await import("./xrpl");
  try {
    const client = await getClient();
    const info = await client.request({ command: "server_info" } as never);
    const server = (info as { result?: { info?: Record<string, unknown> } }).result?.info ?? {};
    const validated = server["validated_ledger"] as
      | { seq?: number; base_fee_xrp?: number; reserve_base_xrp?: number; reserve_inc_xrp?: number }
      | undefined;
    return {
      ledgerIndex: validated?.seq ?? null,
      baseFeeXrp: validated?.base_fee_xrp ?? null,
      reserveBaseXrp: validated?.reserve_base_xrp ?? null,
      reserveIncXrp: validated?.reserve_inc_xrp ?? null,
      loadFactor: (server["load_factor"] as number | undefined) ?? null,
      version: (server["build_version"] as string | undefined) ?? null,
      peers: (server["peers"] as number | undefined) ?? null,
      completeLedgers: (server["complete_ledgers"] as string | undefined) ?? null,
    };
  } catch {
    return null;
  }
}

/** Compact USD formatting for headline metrics: $1.24B, $58.3M, $912.4K. */
export function compactUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const abs = Math.abs(value);
  const unit =
    abs >= 1e12
      ? ["T", 1e12]
      : abs >= 1e9
        ? ["B", 1e9]
        : abs >= 1e6
          ? ["M", 1e6]
          : abs >= 1e3
            ? ["K", 1e3]
            : ["", 1];
  const scaled = value / (unit[1] as number);
  return `$${scaled.toFixed(scaled >= 100 ? 1 : 2)}${unit[0] as string}`;
}

export function compactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(
    value,
  );
}

export function signedPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

export interface XrpChartPoint {
  t: number;
  p: number;
}

export type XrpChartRange = "7" | "90" | "365" | "max";

export type XrpChartSource = "kraken" | "bitstamp" | "binance" | "coingecko";

export interface XrpChartSeries {
  range: XrpChartRange;
  points: XrpChartPoint[];
  first: number | null;
  last: number | null;
  min: number | null;
  max: number | null;
  changePercent: number | null;
  source: XrpChartSource | null;
  fetchedAt: number;
}

const EMPTY_SERIES = (range: XrpChartRange): XrpChartSeries => ({
  range,
  points: [],
  first: null,
  last: null,
  min: null,
  max: null,
  changePercent: null,
  source: null,
  fetchedAt: Date.now(),
});

function summarize(
  range: XrpChartRange,
  points: XrpChartPoint[],
  source: XrpChartSource,
): XrpChartSeries {
  if (points.length < 2) return EMPTY_SERIES(range);
  const values = points.map((point) => point.p);
  const first = values[0] as number;
  const last = values[values.length - 1] as number;
  return {
    range,
    points,
    first,
    last,
    min: Math.min(...values),
    max: Math.max(...values),
    changePercent: first > 0 ? ((last - first) / first) * 100 : null,
    source,
    fetchedAt: Date.now(),
  };
}

/**
 * Kraken public OHLC — XRP/USD spot back to 2017, CORS-enabled and keyless.
 * Returns up to 720 candles per request, so each range picks an interval that
 * covers the whole window.
 */
async function fetchFromKraken(range: XrpChartRange): Promise<XrpChartPoint[]> {
  const interval: Record<XrpChartRange, number> = {
    "7": 60, // hourly
    "90": 240, // 4h
    "365": 1440, // daily
    max: 10080, // weekly
  };
  const minutes = interval[range] ?? 1440;
  const data = await getJson<{
    error?: string[];
    result?: Record<string, (string | number)[][]>;
  }>(`https://api.kraken.com/0/public/OHLC?pair=XRPUSD&interval=${minutes}`);
  const series = Object.entries(data?.result ?? {}).find(([key]) => key !== "last")?.[1];
  const rows = (series ?? [])
    .map((row) => ({ t: Number(row[0]) * 1000, p: Number(row[4]) }))
    .filter((point) => Number.isFinite(point.t) && Number.isFinite(point.p) && point.p > 0);
  return trimToRange(rows, range);
}

/** Bitstamp public OHLC — XRP/USD, CORS-enabled and keyless. */
async function fetchFromBitstamp(range: XrpChartRange): Promise<XrpChartPoint[]> {
  const config: Record<XrpChartRange, { step: number; limit: number }> = {
    "7": { step: 3600, limit: 168 },
    "90": { step: 14400, limit: 540 },
    "365": { step: 86400, limit: 365 },
    max: { step: 259200, limit: 1000 },
  };
  const { step, limit } = config[range] ?? config["365"];
  const data = await getJson<{
    data?: { ohlc?: { timestamp: string; close: string }[] };
  }>(`https://www.bitstamp.net/api/v2/ohlc/xrpusd/?step=${step}&limit=${limit}`);
  return (data?.data?.ohlc ?? [])
    .map((row) => ({ t: Number(row.timestamp) * 1000, p: Number(row.close) }))
    .filter((point) => Number.isFinite(point.t) && Number.isFinite(point.p) && point.p > 0);
}

/** Binance public klines — XRP/USDT from 2018, keyless (blocked in some regions). */
async function fetchFromBinance(range: XrpChartRange): Promise<XrpChartPoint[]> {
  const config: Record<XrpChartRange, { interval: string; limit: number }> = {
    "7": { interval: "1h", limit: 168 },
    "90": { interval: "8h", limit: 270 },
    "365": { interval: "1d", limit: 365 },
    max: { interval: "1w", limit: 1000 },
  };
  const { interval, limit } = config[range] ?? config["365"];
  const rows = await getJson<(string | number)[][]>(
    `https://api.binance.com/api/v3/klines?symbol=XRPUSDT&interval=${interval}&limit=${limit}`,
  );
  return (rows ?? [])
    .map((row) => ({ t: Number(row[0]), p: Number(row[4]) }))
    .filter((point) => Number.isFinite(point.t) && Number.isFinite(point.p));
}

/** CoinGecko market chart — keyless tier is throttled and limited to short windows. */
async function fetchFromCoinGecko(range: XrpChartRange): Promise<XrpChartPoint[]> {
  const data = await getJson<{ prices?: [number, number][] }>(
    `${CG}/coins/ripple/market_chart?vs_currency=usd&days=${range}`,
  );
  return (data?.prices ?? [])
    .filter((row) => Array.isArray(row) && Number.isFinite(row[0]) && Number.isFinite(row[1]))
    .map(([t, p]) => ({ t, p }));
}

const RANGE_MS: Record<Exclude<XrpChartRange, "max">, number> = {
  "7": 7 * 864e5,
  "90": 90 * 864e5,
  "365": 365 * 864e5,
};

/** Providers return fixed candle counts; clip to the requested window. */
function trimToRange(points: XrpChartPoint[], range: XrpChartRange): XrpChartPoint[] {
  if (range === "max") return points;
  const cutoff = Date.now() - RANGE_MS[range];
  const clipped = points.filter((point) => point.t >= cutoff);
  return clipped.length >= 2 ? clipped : points;
}

const PROVIDERS: { source: XrpChartSource; load: (r: XrpChartRange) => Promise<XrpChartPoint[]> }[] =
  [
    { source: "kraken", load: fetchFromKraken },
    { source: "bitstamp", load: fetchFromBitstamp },
    { source: "binance", load: fetchFromBinance },
    { source: "coingecko", load: fetchFromCoinGecko },
  ];

/**
 * Price history only — no account data leaves the device. Kraken XRP/USD is the primary
 * source (full history back to 2017, CORS-enabled, keyless) with Bitstamp, Binance and
 * CoinGecko as fallbacks. Throws only when every provider fails.
 */
export async function fetchXrpChart(range: XrpChartRange): Promise<XrpChartSeries> {
  for (const provider of PROVIDERS) {
    try {
      const points = await provider.load(range);
      if (points.length >= 2) {
        return summarize(range, points.sort((a, b) => a.t - b.t), provider.source);
      }
    } catch {
      // try the next provider
    }
  }

  throw new Error("XRP price history is unavailable from all providers right now.");
}



